<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">常見問題（FAQ）</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="handleCreate">
          <i class="ri-add-line"></i>
          新增 FAQ
        </button>
      </div>
    </div>

    <div class="page-container">
    <!-- 搜尋表單 -->
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchForm.search"
          type="text"
          class="form-control"
          placeholder="搜尋問題、答案或關鍵字..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="searchForm.category" class="form-control" @change="handleSearch">
          <option value="">全部分類</option>
          <option value="營業資訊">營業資訊</option>
          <option value="會員相關">會員相關</option>
          <option value="點數相關">點數相關</option>
          <option value="優惠券相關">優惠券相關</option>
          <option value="訂單相關">訂單相關</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="searchForm.enabled" class="form-control" @change="handleSearch">
          <option value="">全部狀態</option>
          <option :value="true">啟用</option>
          <option :value="false">停用</option>
        </select>
      </div>
      <button class="btn btn-secondary" @click="handleReset">
        <i class="ri-refresh-line"></i>
        重置
      </button>
    </div>

    <!-- 載入狀態 -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <!-- FAQ 列表 -->
    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>分類</th>
            <th>問題</th>
            <th>答案</th>
            <th>關鍵字</th>
            <th>瀏覽次數</th>
            <th>排序</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="faq in faqs" :key="faq.id">
            <td>{{ faq.category || '-' }}</td>
            <td>{{ faq.question }}</td>
            <td class="answer-cell">{{ truncateText(faq.answer, 50) }}</td>
            <td>{{ faq.keywords || '-' }}</td>
            <td class="text-center">{{ faq.viewCount || 0 }}</td>
            <td class="text-center">{{ faq.displayOrder || 0 }}</td>
            <td class="text-center">
              <span class="status-badge" :class="faq.enabled ? 'status-active' : 'status-inactive'">
                {{ faq.enabled ? '啟用' : '停用' }}
              </span>
            </td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-primary"
                  @click="handleEdit(faq)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                  編輯
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(faq)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="faqs.length === 0" class="empty-state">
        <p>暫無 FAQ</p>
      </div>
    </div>

    <!-- 分頁 -->
    <ListPagination
      v-if="pagination.totalPages > 1"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      :total="pagination.total"
      @change="handlePageChange"
    />

    </div>
  </div>
</template>

<script>
import { supportService } from '@/api/modules/support.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'AutoReplyFaqs',
  data() {
    return {
      loading: false,
      faqs: [],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      },
      searchForm: {
        search: '',
        category: '',
        enabled: ''
      }
    };
  },
  mounted() {
    this.loadFaqs();
  },
  methods: {
    async loadFaqs() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          ...this.searchForm
        };
        const response = await supportService.listFaqs(params);
        this.faqs = response?.data?.items || [];

        // 後端 pagination 欄位為 totalItems / totalPages
        if (response?.data?.pagination) {
          this.pagination = {
            ...this.pagination,
            ...response.data.pagination,
            total: response.data.pagination.totalItems ?? this.pagination.total
          };
        }
      } catch (error) {
        console.error('載入 FAQ 失敗:', error);
        showError('載入 FAQ 失敗', '');
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.pagination.page = 1;
      this.loadFaqs();
    },

    handleReset() {
      this.searchForm = {
        search: '',
        category: '',
        enabled: ''
      };
      this.pagination.page = 1;
      this.loadFaqs();
    },

    handlePageChange(page) {
      this.pagination.page = page;
      this.loadFaqs();
    },

    handleCreate() {
      this.$router.push({ name: 'AutoReplyFaqCreate' });
    },

    handleEdit(faq) {
      this.$router.push({ name: 'AutoReplyFaqEdit', params: { id: faq.id } });
    },

    async handleDelete(faq) {
      const confirmed = await showDeleteConfirm(
        '確定要刪除 FAQ 嗎？',
        `FAQ「${faq.question}」將被永久刪除，此操作無法復原。`
      );
      if (!confirmed) {
        return;
      }

      try {
        await supportService.deleteFaq(faq.id);
        await showSuccess('刪除成功', '');
        this.loadFaqs();
      } catch (error) {
        console.error('刪除 FAQ 失敗:', error);
        showError('刪除 FAQ 失敗', '');
      }
    },

    async handleToggleEnabled(faq) {
      try {
        await supportService.updateFaq(faq.id, {
          enabled: !faq.enabled
        });
        await showSuccess('更新成功', '');
        this.loadFaqs();
      } catch (error) {
        console.error('更新 FAQ 失敗:', error);
        showError('更新 FAQ 失敗', '');
        // 恢復原狀態
        this.loadFaqs();
      }
    },

    truncateText(text, length) {
      if (!text) return '-';
      return text.length > length ? text.substring(0, length) + '...' : text;
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.answer-cell {
  max-width: 300px;
}

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}
.status-active {
  background: #dcfce7;
  color: #15803d;
}
.status-inactive {
  background: #f3f4f6;
  color: #6b7280;
}

.action-buttons {
  display: flex;
  gap: 4px;
}
</style>
